/* SalesDashboard — landing page of the Sales Portal.
 *
 * iter98.52 — Layout (matches the doctor-portal premium aesthetic):
 *   1. 6 KPI cards in a single responsive row
 *   2. Sales funnel waterfall (7 pipeline stages, count + cumulative
 *      drop-off bars)
 *   3. Top reps leaderboard (manager view) or rep's own monthly stats
 *      (rep view)
 *   4. Doctors at risk — stalled negotiations, no contact in 30 days
 *   5. Recent activity feed (calls, demos, contracts signed, cases)
 *
 * Data source: GET /api/sales/dashboard
 *
 * Aligner-specific framing: every label is about doctor activation +
 * case volume + per-case revenue. Nothing here says "deals closed".
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Users, UserCheck, Stack, Coins, Target, Warning,
    ArrowUpRight, ArrowDownRight, Clock, Phone, EnvelopeSimple, ChartLine,
    CheckCircle, Circle, FileText, Calendar, Lightning, Star, CheckSquare,
} from "@phosphor-icons/react";


/* Stage metadata — drives funnel labels, dot colors, and the friendly
   name we use everywhere a stage is rendered. */
const STAGE_META = {
    prospect:     { label: "Prospect",      color: "#94A3B8" },  // slate
    discovery:    { label: "Discovery",     color: "#0EA5E9" },  // sky
    demo:         { label: "Lab Demo",      color: "#6366F1" },  // indigo
    trial:        { label: "Case Trial",    color: "#F59E0B" },  // amber
    negotiation:  { label: "Negotiation",   color: "#EC4899" },  // pink
    contract:     { label: "Contract",      color: "#10B981" },  // emerald
    active:       { label: "Active",        color: "#059669" },  // emerald-dk
};

const STAGE_ORDER = ["prospect", "discovery", "demo", "trial", "negotiation", "contract", "active"];


function fmtINR(n) {
    if (n === null || n === undefined || isNaN(n)) return "—";
    const v = Number(n);
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000)   return `₹${(v / 100000).toFixed(1)}L`;
    if (v >= 1000)     return `₹${(v / 1000).toFixed(0)}k`;
    return `₹${v}`;
}


export default function SalesDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [taskBuckets, setTaskBuckets] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/sales/dashboard"),
            api.get("/sales/tasks"),
        ])
            .then(([d, t]) => {
                if (!alive) return;
                setData(d.data);
                setTaskBuckets(t.data);
            })
            .catch((e) => { /* eslint-disable-next-line no-console */ console.error("dashboard fetch:", e); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return <div className="p-8 text-sm text-muted-foreground">Loading dashboard…</div>;
    }
    if (!data) {
        return <div className="p-8 text-sm text-muted-foreground">No data yet.</div>;
    }

    const { kpis, funnel, leaderboard, at_risk, recent_activity, is_manager } = data;
    const maxFunnelCount = Math.max(1, ...funnel.map((f) => f.count));

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="sales-dashboard">

            {/* ── Page header ── */}
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Sales overview</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {is_manager
                            ? "All territories · all reps · this month so far"
                            : "Your assigned prospects · this month so far"}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate("/sales/pipeline")}
                    className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline"
                    data-testid="dash-open-pipeline-btn"
                >
                    Open pipeline <ArrowUpRight size={12} weight="bold" />
                </button>
            </div>

            {/* ── KPI strip — 6 cards ── */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="sales-kpi-strip">
                <KpiCard
                    label="Total prospects"
                    value={kpis.total_prospects}
                    icon={Users}
                    tone="slate"
                    testid="kpi-total-prospects"
                />
                <KpiCard
                    label="Active doctors"
                    value={kpis.active_doctors}
                    icon={UserCheck}
                    tone="emerald"
                    testid="kpi-active-doctors"
                />
                <KpiCard
                    label="Cases / month"
                    value={kpis.cases_per_month_total}
                    icon={Stack}
                    tone="primary"
                    testid="kpi-cases-month"
                />
                <KpiCard
                    label="Monthly revenue"
                    value={fmtINR(kpis.monthly_revenue)}
                    icon={Coins}
                    tone="amber"
                    testid="kpi-monthly-revenue"
                />
                <KpiCard
                    label="Avg cases / doctor"
                    value={kpis.avg_cases_per_doctor}
                    sub={`Target: 15`}
                    icon={Target}
                    tone="indigo"
                    testid="kpi-avg-cases"
                />
                <KpiCard
                    label="At-risk doctors"
                    value={kpis.at_risk_count}
                    icon={Warning}
                    tone="rose"
                    testid="kpi-at-risk"
                    danger={kpis.at_risk_count > 0}
                />
            </div>

            {/* ── Funnel — full-width waterfall ── */}
            <div className="bg-white rounded-2xl border border-border p-5" data-testid="sales-funnel">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h2 className="font-display text-base font-bold">Sales funnel</h2>
                        <p className="text-[11px] text-muted-foreground">Doctors at each pipeline stage. Bigger bar = more prospects parked there.</p>
                    </div>
                    <button onClick={() => navigate("/sales/pipeline")} className="text-[11px] font-semibold text-primary inline-flex items-center gap-1" data-testid="funnel-open-pipeline-btn">
                        See in kanban <ArrowUpRight size={11} weight="bold" />
                    </button>
                </div>
                <div className="space-y-2">
                    {funnel.map((f, idx) => {
                        const meta = STAGE_META[f.stage] || STAGE_META.prospect;
                        const widthPct = (f.count / maxFunnelCount) * 100;
                        const prev = idx > 0 ? funnel[idx - 1].count : null;
                        const dropoff = prev !== null && prev > 0 ? Math.round(((prev - f.count) / prev) * 100) : null;
                        return (
                            <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-row-${f.stage}`}>
                                <div className="w-24 shrink-0 text-xs font-semibold flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                                    {meta.label}
                                </div>
                                <div className="flex-1 bg-secondary/50 rounded h-7 relative overflow-hidden">
                                    <div
                                        className="h-full rounded transition-all duration-500"
                                        style={{ width: `${widthPct}%`, background: meta.color, opacity: 0.85 }}
                                    />
                                    <div className="absolute inset-0 px-3 flex items-center text-[11px] font-bold text-white drop-shadow-sm" style={{ minWidth: widthPct > 0 ? "auto" : 0 }}>
                                        {f.count > 0 ? `${f.count}` : ""}
                                    </div>
                                </div>
                                <div className="w-24 shrink-0 text-right">
                                    <div className="text-xs font-semibold tabular-nums">{fmtINR(f.value)}</div>
                                    {dropoff !== null && dropoff > 0 && idx > 0 && (
                                        <div className="text-[10px] text-muted-foreground flex items-center justify-end gap-0.5">
                                            <ArrowDownRight size={9} /> {dropoff}%
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── 3-column: Tasks + Leaderboard + At-risk ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* iter98.53 — My tasks panel */}
                <TaskBucketsCard taskBuckets={taskBuckets} onNavigate={() => navigate("/sales/tasks")} onOpenProspect={(pid) => navigate(`/sales/prospects/${pid}`)} />

                {/* Top reps leaderboard */}
                <div className="bg-white rounded-2xl border border-border p-5" data-testid="sales-leaderboard">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div>
                            <h2 className="font-display text-base font-bold">Top reps this month</h2>
                            <p className="text-[11px] text-muted-foreground">Ranked by doctors activated · cases submitted · monthly revenue.</p>
                        </div>
                        <Star size={16} weight="duotone" className="text-amber-500" />
                    </div>
                    {leaderboard.length === 0 ? (
                        <div className="py-6 text-center text-xs text-muted-foreground">
                            No rep activity yet this month.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {leaderboard.map((r, idx) => (
                                <div key={r.rep_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/40" data-testid={`leaderboard-row-${idx + 1}`}>
                                    <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold ${
                                        idx === 0 ? "bg-amber-100 text-amber-700" :
                                        idx === 1 ? "bg-slate-100 text-slate-700" :
                                        idx === 2 ? "bg-orange-100 text-orange-700" :
                                        "bg-secondary text-muted-foreground"
                                    }`}>
                                        #{idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate">{r.rep_name}</div>
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{r.territory || "—"}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-sm font-bold text-primary tabular-nums">{r.doctors_activated_mo}</div>
                                        <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Activated</div>
                                    </div>
                                    <div className="text-right shrink-0 hidden sm:block">
                                        <div className="text-sm font-bold tabular-nums">{r.cases_submitted_mo}</div>
                                        <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Cases</div>
                                    </div>
                                    <div className="text-right shrink-0 hidden md:block">
                                        <div className="text-sm font-bold tabular-nums">{fmtINR(r.monthly_revenue)}</div>
                                        <div className="text-[9px] text-muted-foreground uppercase tracking-wider">Revenue</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* At-risk */}
                <div className="bg-white rounded-2xl border border-border p-5" data-testid="sales-at-risk">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div>
                            <h2 className="font-display text-base font-bold">Doctors at risk</h2>
                            <p className="text-[11px] text-muted-foreground">Stalled prospects · no contact in 30+ days · flagged red.</p>
                        </div>
                        <Warning size={16} weight="duotone" className="text-rose-600" />
                    </div>
                    {at_risk.length === 0 ? (
                        <div className="py-6 text-center text-xs text-muted-foreground inline-flex items-center gap-1.5 w-full justify-center">
                            <CheckCircle size={14} className="text-emerald-600" weight="fill" />
                            All your prospects are healthy — nothing on fire.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {at_risk.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => navigate(`/sales/prospects/${p.id}`)}
                                    className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-rose-100 hover:bg-rose-50 transition-colors"
                                    data-testid={`at-risk-row-${p.id}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-rose-100 grid place-items-center shrink-0">
                                        <Warning size={14} className="text-rose-600" weight="fill" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate">{p.clinic_name}</div>
                                        <div className="text-[10px] text-muted-foreground truncate">
                                            {p.doctor_name} · {STAGE_META[p.stage]?.label || p.stage}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[10px] font-semibold text-rose-700 uppercase tracking-wider">
                                            {p.reason === "no_contact_30_days" ? "Stale 30d+" : "Red flag"}
                                        </div>
                                        {p.days_stale !== null && (
                                            <div className="text-[10px] text-muted-foreground tabular-nums">
                                                {p.days_stale}d
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Recent activity feed ── */}
            <div className="bg-white rounded-2xl border border-border p-5" data-testid="sales-recent-activity">
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div>
                        <h2 className="font-display text-base font-bold">Recent activity</h2>
                        <p className="text-[11px] text-muted-foreground">Calls, demos, contracts &amp; cases — latest 15.</p>
                    </div>
                    <Lightning size={16} weight="duotone" className="text-primary" />
                </div>
                {recent_activity.length === 0 ? (
                    <div className="py-6 text-center text-xs text-muted-foreground">Nothing logged yet — start by calling a prospect.</div>
                ) : (
                    <div className="space-y-1">
                        {recent_activity.map((a) => (
                            <ActivityRow key={a.id} a={a} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


/* ─────────────── KpiCard ─────────────── */
const KPI_TONE = {
    slate:   "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    primary: "bg-primary/10 text-primary",
    amber:   "bg-amber-50 text-amber-700",
    indigo:  "bg-indigo-50 text-indigo-700",
    rose:    "bg-rose-50 text-rose-700",
};
function KpiCard({ label, value, sub, icon: Icon, tone, testid, danger }) {
    return (
        <div
            className={`rounded-2xl border bg-white p-4 ${danger ? "border-rose-200" : "border-border"}`}
            data-testid={testid}
        >
            <div className={`w-8 h-8 rounded-lg grid place-items-center mb-3 ${KPI_TONE[tone] || KPI_TONE.slate}`}>
                <Icon size={16} weight="duotone" />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
            <div className={`font-display text-2xl font-bold tabular-nums mt-1 ${danger ? "text-rose-600" : ""}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
        </div>
    );
}


/* ─────────────── ActivityRow ─────────────── */
const ACTIVITY_ICON = {
    call:            Phone,
    email:           EnvelopeSimple,
    meeting:         Calendar,
    demo:            ChartLine,
    note:            FileText,
    stage_change:    ArrowUpRight,
    case_submitted:  Stack,
    proposal_sent:   FileText,
    contract_signed: CheckCircle,
};
const ACTIVITY_TINT = {
    call:            "bg-sky-50 text-sky-700",
    email:           "bg-indigo-50 text-indigo-700",
    meeting:         "bg-violet-50 text-violet-700",
    demo:            "bg-amber-50 text-amber-700",
    note:            "bg-slate-50 text-slate-700",
    stage_change:    "bg-primary/10 text-primary",
    case_submitted:  "bg-emerald-50 text-emerald-700",
    proposal_sent:   "bg-rose-50 text-rose-700",
    contract_signed: "bg-emerald-50 text-emerald-700",
};

function timeAgo(iso) {
    if (!iso) return "—";
    try {
        const t = new Date(iso).getTime();
        const diffMin = (Date.now() - t) / 60000;
        if (diffMin < 1) return "just now";
        if (diffMin < 60) return `${Math.round(diffMin)}m ago`;
        const diffH = diffMin / 60;
        if (diffH < 24) return `${Math.round(diffH)}h ago`;
        const diffD = diffH / 24;
        if (diffD < 7) return `${Math.round(diffD)}d ago`;
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch { return "—"; }
}

function ActivityRow({ a }) {
    const Icon = ACTIVITY_ICON[a.type] || FileText;
    const tint = ACTIVITY_TINT[a.type] || "bg-slate-50 text-slate-700";
    return (
        <div className="flex items-center gap-3 py-2 px-2 hover:bg-secondary/30 rounded-lg" data-testid={`recent-activity-${a.id}`}>
            <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${tint}`}>
                <Icon size={13} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs truncate">
                    <span className="font-semibold">{a.clinic_name || "—"}</span>
                    <span className="text-muted-foreground"> · {a.summary}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                    {a.rep_name && <>{a.rep_name} · </>}{a.doctor_name}
                </div>
            </div>
            <div className="text-[10px] text-muted-foreground tabular-nums shrink-0 inline-flex items-center gap-1">
                <Clock size={10} /> {timeAgo(a.when)}
            </div>
        </div>
    );
}



/* ─────────────── TaskBucketsCard (Dashboard tile) ─────────────── */
function TaskBucketsCard({ taskBuckets, onNavigate, onOpenProspect }) {
    const overdue = taskBuckets?.buckets?.overdue || [];
    const today = taskBuckets?.buckets?.today || [];
    const counts = taskBuckets?.counts || { overdue: 0, today: 0, upcoming: 0, open_total: 0 };
    const showItems = [...overdue.slice(0, 3), ...today.slice(0, Math.max(0, 4 - overdue.length))].slice(0, 4);

    return (
        <div className="bg-white rounded-2xl border border-border p-5" data-testid="sales-dash-tasks">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                    <h2 className="font-display text-base font-bold inline-flex items-center gap-1.5">
                        <CheckSquare size={16} weight="duotone" className="text-primary" />
                        My tasks
                    </h2>
                    <p className="text-[11px] text-muted-foreground">
                        {counts.overdue > 0 && <span className="text-rose-600 font-bold">{counts.overdue} overdue</span>}
                        {counts.overdue > 0 && counts.today > 0 && " · "}
                        {counts.today > 0 && <span className="text-amber-600 font-bold">{counts.today} due today</span>}
                        {counts.overdue === 0 && counts.today === 0 && (counts.open_total === 0 ? "All caught up." : `${counts.open_total} upcoming`)}
                    </p>
                </div>
                <button
                    onClick={onNavigate}
                    className="text-[11px] font-semibold text-primary inline-flex items-center gap-1 hover:underline"
                    data-testid="dash-tasks-open-all"
                >
                    Open all <ArrowUpRight size={11} weight="bold" />
                </button>
            </div>

            {showItems.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground inline-flex items-center gap-1.5 w-full justify-center" data-testid="dash-tasks-empty">
                    <CheckCircle size={14} className="text-emerald-600" weight="fill" />
                    {counts.open_total === 0 ? "All caught up — no open tasks." : "Nothing due today, you're ahead."}
                </div>
            ) : (
                <div className="space-y-2">
                    {showItems.map((t) => {
                        const overdueRow = overdue.includes(t);
                        return (
                            <button
                                key={t.id}
                                onClick={() => onOpenProspect(t.prospect_id)}
                                className={`w-full text-left flex items-center gap-2 p-2 rounded-lg border ${overdueRow ? "border-rose-200 bg-rose-50/40" : "border-border bg-secondary/20"} hover:bg-secondary/40 transition-colors`}
                                data-testid={`dash-task-${t.id}`}
                            >
                                <Circle size={13} className={overdueRow ? "text-rose-600 shrink-0" : "text-muted-foreground shrink-0"} weight={overdueRow ? "fill" : "regular"} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold truncate">{t.title}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">
                                        {t.clinic_name || "—"}
                                    </div>
                                </div>
                                <div className={`text-[10px] font-bold shrink-0 ${overdueRow ? "text-rose-600" : "text-amber-600"}`}>
                                    {overdueRow ? "Overdue" : "Today"}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
